//-----------------------------------------------------------------------
// Copyright © 2022 ManpowerGroup. All Rights Reserved.
//-----------------------------------------------------------------------

/* eslint-disable prettier/prettier */
import React, { useContext, useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Card, Col, Container, Row, Form, Button } from "react-bootstrap";
import { Context } from "../../src/common/context/Context";
import { CandidatePageActions } from "../../src/candidatePageActions";
import { CandidateSearchActions } from "./candidateSearchActions";
import CandidateSearchResults from "./candidateSearchResults";
import AlertMessage from "../common/alertMessages/alertMessages";
import ErrorAlertMessage from "../common/alertMessages/errorAlertMessage";

const CandidateSearch = ({ candidateCMSData }) => {
    const context = useContext(Context);

    const [candidateSearchCMSData, setCandidateSearchCMSData] = useState({});
    const [commonComponentCMSData, setCommonComponentCMSData] = useState({});
    const [validationMsgCMSData, setValidationMsgCMSData] = useState({});

    const [errorAlert, setErrorAlert] = useState({
        showAlert: false
    })

    const searchInitialState = {
        firstName: '',
        lastName: '',
        email: '',
        rightTrackKey: '',
        companyName: '',
        programStatus: '',
        country: '',
        persona: '',
        rtProgramTemplate: '',
        genericAccount: '',
        programClass: '',
        serviceOffice: '',
        cmc: ''
    }

    const [serviceOfficeList, setServiceOfficeList] = useState([]);
    const [cmcList, setCMCList] = useState([]);
    const [candidateSearchForm, setCandidateSearchForm] = useState(searchInitialState);
    const [searchResults, setSearchResults] = useState([]);
    const [showSearchResults, setShowSearchResults] = useState(false);
    const [searchDisabled, setSearchDisabled] = useState(true);
    const [error, setError] = useState({
        firstName: '',
        lastName: ''
    });
    const [candidateSearchJson, setCandidateSearchJson] = useState({});
    const [alert, setAlert] = useState({
        message: "",
        type: "",
        showAlert: false
    })

    const fetchCandidateSearchHeadingLabel = (key) => {
        if (candidateSearchCMSData && candidateSearchCMSData.Labels) {
            return candidateSearchCMSData.Labels.find((el) => el.Key === key)?.Phrase || '';
        }
        return '';
    }

    const fetchCommonComponentLabel = (key) => {
        if (commonComponentCMSData && commonComponentCMSData.Labels) {
            return commonComponentCMSData.Labels.find((el) => el.Key === key)?.Phrase || '';
        }
        return '';
    }

    const fetchValidationMsgCMSData = (key) => {
        if (validationMsgCMSData && validationMsgCMSData.Labels) {
            return validationMsgCMSData.Labels.find((el) => el.Key === key)?.Phrase || '';
        }
        return '';
    }

    const canBeSubmitted = () => {
        let fieldsNotEmpty = candidateSearchForm.firstName !== '' || candidateSearchForm.lastName !== '' || candidateSearchForm.email !== ''
            || candidateSearchForm.rightTrackKey !== '' || candidateSearchForm.companyName !== ''
            || candidateSearchForm.programStatus !== '' || candidateSearchForm.country !== ''
            || candidateSearchForm.persona !== '';

        let hasError = error.firstName !== '' || error.lastName !== ''

        let disable = fieldsNotEmpty && !hasError;
        setSearchDisabled(!disable);
    }

    const getServiceOffice = async (countryCode) => {

        let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));

        await CandidatePageActions.GetServiceOffice(countryCode).then(
            (res) => {
                if (res.status === 200 || res.status === 204) {
                    setServiceOfficeList(res.data && res.data)
                }
                else {
                    refErrorMessageAlert.showAlert = true;
                    setErrorAlert(refErrorMessageAlert);
                    window.scrollTo(0, 0);
                }
            },
            (err) => {
                console.log(err);
            }
        );
    }


    const getCMC = async (rightOfficeId) => {

        let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));

        CandidatePageActions.GetRightOfficeId(rightOfficeId).then(
            (res) => {
                if (res.status === 200 || res.status === 204) {
                    setCMCList(res.data && res.data)
                }
                else {
                    refErrorMessageAlert.showAlert = true;
                    setErrorAlert(refErrorMessageAlert);
                    window.scrollTo(0, 0);
                }
            },
            (err) => {
                console.log(err);
            }
        );
    }

    const handleFirstNameChange = (e) => {
        let name = e.target.value;
        let errorMessage = '';
        if (name !== '' && name.length < 1) {
            errorMessage = fetchValidationMsgCMSData('CandidateSearch.NameValidationMessage')
        }
        setError({
            ...error,
            [e.target.name]: errorMessage
        });
        handleChange(e);
    }

    const handleLastNameChange = (e) => {
        let name = e.target.value;
        let errorMessage = '';
        if (name !== '' && name.length < 1) {
            errorMessage = fetchValidationMsgCMSData('CandidateSearch.NameValidationMessage')
        }
        setError({
            ...error,
            [e.target.name]: errorMessage
        });
        handleChange(e);
    }


    const handleCountryChange = (e) => {
        let country = e.target.value;
        handleChange(e);
        getServiceOffice(country);

    }

    const handleServiceOfficeChange = (e) => {
        let office = e.target.value;
        handleChange(e);
        getCMC(office);
    }

    const handleChange = (event) => {
        setCandidateSearchForm({
            ...candidateSearchForm,
            [event.target.name]: event.target.value
        });
    }

    const handleClearAll = () => {
        setCandidateSearchForm(searchInitialState);
        setShowSearchResults(false);
    }

    const showModal = () => {
        let refAlert = JSON.parse(JSON.stringify(alert));
        refAlert.showAlert = false;
        setAlert(refAlert)
    }

    const handleSearchSubmit = async () => {
        const searchFormData = new FormData();
        searchFormData.append("FirstName", candidateSearchForm.firstName)
        searchFormData.append("LastName", candidateSearchForm.lastName)
        searchFormData.append("EmailAddress", candidateSearchForm.email)
        searchFormData.append("KeyNumber", candidateSearchForm.rightTrackKey)
        searchFormData.append("CompanyName", candidateSearchForm.companyName)
        searchFormData.append("Status", candidateSearchForm.programStatus)
        searchFormData.append("ProgramCountryCode", candidateSearchForm.country)
        searchFormData.append("RTPersonaId", candidateSearchForm.persona)
        searchFormData.append("IsGeneric", candidateSearchForm.genericAccount)
        searchFormData.append("ProgramClassId", candidateSearchForm.programClass)
        searchFormData.append("ServiceOfficeId", candidateSearchForm.serviceOffice)
        searchFormData.append("CMCId", candidateSearchForm.cmc)

        const searchString = new URLSearchParams(searchFormData).toString();
        let refAlert = JSON.parse(JSON.stringify(alert));
        CandidateSearchActions.GetCandidateSearch(searchString).then(
            (res) => {
                if (res.status === 200) {
                    setSearchResults(res?.data);
                    setShowSearchResults(true);
                }
                else if (res.status === 500 || res.status === 400 || res.status === 401) {
                    refAlert.showAlert = true;
                    refAlert.type = "danger";
                    refAlert.message = fetchValidationMsgCMSData('CandidateSearch.SearchErrorMessage');
                    setShowSearchResults(false);
                } else if (res.status === 204) {
                    refAlert.showAlert = true;
                    refAlert.type = "danger";
                    refAlert.message = fetchValidationMsgCMSData('CandidateSearch.SearchNoResponseMessage');
                    setShowSearchResults(false);
                }
                setAlert(refAlert)
            },
            (err) => {
                console.log(err);
            }
        );
    }

    const showErrorModal = () => {
        let refErrorMessageAlert = JSON.parse(JSON.stringify(errorAlert));
        refErrorMessageAlert.showAlert = false;
        setErrorAlert(refErrorMessageAlert)
    }

    useEffect(() => {
        canBeSubmitted();
    }, [candidateSearchForm, error]);

    useEffect(() => {
        setCandidateSearchCMSData(candidateCMSData?.pageContent["CandidateSearch.CandidateSearchHeading"]);
        setCommonComponentCMSData(candidateCMSData?.pageContent["CommonComponent"]);
        setValidationMsgCMSData(candidateCMSData?.pageContent["CandidateSearchValidationMsg"]);
    }, [candidateCMSData]);

    return (
        <React.Fragment>
            <Helmet title="Cards" />
            <Container fluid className="p-0">
                <h1 className="h3 m-3">{candidateSearchCMSData?.Title}</h1>

                <Row>
                    <Col lg="12">
                        <Card>
                            <Card.Body>
                                {errorAlert?.showAlert &&
                                    <ErrorAlertMessage alertMessage="AllPages.ErrorAlertMessage" showAlert={showErrorModal} />
                                }
                                <Form>
                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>{fetchCandidateSearchHeadingLabel('CandidateSearch.SearchField.FirstName')}</Form.Label>
                                                <Form.Control type="text" name="firstName" placeholder={fetchCandidateSearchHeadingLabel('CandidateSearch.PlaceHolder.FirstName')}
                                                    value={candidateSearchForm.firstName} onChange={handleFirstNameChange}
                                                    isInvalid={!!error.firstName}
                                                    maxLength={50}
                                                />
                                                <Form.Control.Feedback type='invalid'>
                                                    {error.firstName}
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>{fetchCandidateSearchHeadingLabel('CandidateSearch.SearchField.LastName')}</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="lastName"
                                                    placeholder={fetchCandidateSearchHeadingLabel('CandidateSearch.PlaceHolder.LastName')}
                                                    value={candidateSearchForm.lastName}
                                                    onChange={handleLastNameChange}
                                                    isInvalid={!!error.lastName}
                                                    maxLength={50}
                                                />
                                                <Form.Control.Feedback type='invalid'>
                                                    {error.lastName}
                                                </Form.Control.Feedback>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>{fetchCandidateSearchHeadingLabel('CandidateSearch.SearchField.EmailAddress')}</Form.Label>
                                                <Form.Control type="email" name="email" placeholder={fetchCandidateSearchHeadingLabel('CandidateSearch.PlaceHolder.EmailAddress')}
                                                    value={candidateSearchForm.email} onChange={handleChange} maxLength={320} />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>{fetchCandidateSearchHeadingLabel('CandidateSearch.SearchField.RightTrackKeyNumber')}</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="rightTrackKey"
                                                    placeholder={fetchCandidateSearchHeadingLabel('CandidateSearch.PlaceHolder.RightTrackKeyNumber')}
                                                    value={candidateSearchForm.rightTrackKey}
                                                    onChange={handleChange}
                                                    maxLength={50}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>{fetchCandidateSearchHeadingLabel('CandidateSearch.SearchField.CompanyName')}</Form.Label>
                                                <Form.Control type="text" name="companyName" placeholder={fetchCandidateSearchHeadingLabel('CandidateSearch.PlaceHolder.CompanyName')}
                                                    value={candidateSearchForm.companyName} onChange={handleChange} maxLength={100} />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>{fetchCandidateSearchHeadingLabel('CandidateSearch.SearchField.ProgramStatus')}</Form.Label>
                                                <Form.Select name="programStatus" value={candidateSearchForm.programStatus} onChange={handleChange} aria-label="Program Status Select">
                                                    <option value=''>{fetchCandidateSearchHeadingLabel('CandidateSearch.PlaceHolder.ProgramStatus')}</option>
                                                    {context?.programStatusList?.map((item) => (
                                                        <option key={item?.id} value={item?.status}>
                                                            {item?.status}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>{fetchCandidateSearchHeadingLabel('CandidateSearch.SearchField.Country')}</Form.Label>
                                                <Form.Select name="country" value={candidateSearchForm.country} onChange={handleCountryChange} aria-label="Country Select">
                                                    <option value=''>{fetchCandidateSearchHeadingLabel('CandidateSearch.PlaceHolder.Country')}</option>
                                                    {context?.countryList?.map((item) => (
                                                        <option key={item?.standardCodeValue} value={item?.standardCodeValue}>
                                                            {item?.title}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>{fetchCandidateSearchHeadingLabel('CandidateSearch.SearchField.Persona')}</Form.Label>
                                                <Form.Select name="persona" value={candidateSearchForm.persona} onChange={handleChange} aria-label="Persona Select">
                                                    <option value=''>{fetchCandidateSearchHeadingLabel('CandidateSearch.PlaceHolder.Persona')}</option>
                                                    {context?.rtPersona?.map((item) => (
                                                        <option key={item?.rtPersonaId} value={item?.rtPersonaId}>
                                                            {item?.rtPersonaName}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>{fetchCandidateSearchHeadingLabel('CandidateSearch.SearchField.RTProgramTemplate')}</Form.Label>
                                                <Form.Select name="rtProgramTemplate" value={candidateSearchForm.rtProgramTemplate} onChange={handleChange} disabled={candidateSearchForm.country === ''} aria-label="Program Template Select">
                                                    <option value=''>{fetchCandidateSearchHeadingLabel('CandidateSearch.PlaceHolder.RTProgramTemplate')}</option>
                                                    {context?.rtProgramTemplate?.map((item) => (
                                                        <option key={item?.id} value={item?.id}>
                                                            {item?.rtProgramTemplateName}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>{fetchCandidateSearchHeadingLabel('CandidateSearch.SearchField.GenericAccounts')}</Form.Label>
                                                <Form.Select name="genericAccount" value={candidateSearchForm.genericAccount} onChange={handleChange} disabled={candidateSearchForm.country === ''} aria-label="Generic Account Select">
                                                    <option value=''>{fetchCandidateSearchHeadingLabel('CandidateSearch.PlaceHolder.GenericAccounts')}</option>
                                                    <option value='1'>{fetchCommonComponentLabel('Common.Yes')}</option>
                                                    <option value='0'>{fetchCommonComponentLabel('Common.No')}</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>{fetchCandidateSearchHeadingLabel('CandidateSearch.SearchField.ProgramClass')}</Form.Label>
                                                <Form.Select name="programClass" value={candidateSearchForm.programClass} onChange={handleChange} disabled={candidateSearchForm.country === ''} aria-label="Program Class Select">
                                                    <option value=''>{fetchCandidateSearchHeadingLabel('CandidateSearch.PlaceHolder.ProgramClass')}</option>
                                                    {context?.rtProgramClass?.map((item) => (
                                                        <option key={item?.programclassId} value={item?.programclassId}>
                                                            {item?.programClass}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>{fetchCandidateSearchHeadingLabel('CandidateSearch.SearchField.ServiceOffice')}</Form.Label>
                                                <Form.Select name="serviceOffice" value={candidateSearchForm.serviceOffice} disabled={candidateSearchForm.country === ''} onChange={handleServiceOfficeChange} aria-label="Service Office Select">
                                                    <option value=''>{fetchCandidateSearchHeadingLabel('CandidateSearch.PlaceHolder.ServiceOffice')}</option>
                                                    {serviceOfficeList?.map((item) => (
                                                        <option key={item?.rightOfficeId} value={item?.rightOfficeId}>
                                                            {item?.rightOfficeName}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>{fetchCandidateSearchHeadingLabel('CandidateSearch.SearchField.CMC')}</Form.Label>
                                                <Form.Select name="cmc" value={candidateSearchForm.cmc} onChange={handleChange} disabled={candidateSearchForm.country === '' || candidateSearchForm.serviceOffice === ''} aria-label="CMC Select">
                                                    <option value=''>{fetchCandidateSearchHeadingLabel('CandidateSearch.PlaceHolder.CMC')}</option>
                                                    {cmcList?.map((item) => (
                                                        <option key={item?.id} value={item?.id}>
                                                            {item?.lastName + ',' + item?.firstName}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    {alert?.showAlert &&
                                        <AlertMessage alertMessage={alert?.message} alertType={alert?.type} showAlert={showModal} />
                                    }
                                    <Button variant="primary" className="me-3" onClick={handleSearchSubmit} disabled={searchDisabled}>{fetchCommonComponentLabel('Common.Search')}</Button>
                                    <Button variant="primary" onClick={handleClearAll}>{fetchCommonComponentLabel('Common.Cancel')}</Button>
                                </Form>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
                {showSearchResults &&
                    <Row>
                        <CandidateSearchResults candidateCMSData={candidateCMSData} searchData={searchResults} />
                    </Row>
                }
            </Container>
        </React.Fragment>
    );
}

export default CandidateSearch;
